import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuthCtx } from "@/lib/auth-context";
import { Sparkles, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { lovable } from "@/integrations/lovable";
import { seo } from "@/lib/seo";
import { AuthSkeleton } from "@/components/skeletons";
import { sendWelcomeEmail } from "@/lib/welcome-email.functions";
import { Mail, MailCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/auth")({
  head: () =>
    seo({
      path: "/auth",
      title: "Entrar — Seu Bolso Tranquilo",
      description: "Acesse sua conta ou crie uma nova para começar a organizar suas finanças.",
      noindex: true,
    }),
  component: AuthPage,
  pendingComponent: AuthSkeleton,
});

function AuthPage() {
  const navigate = useNavigate();
  const { user, signIn, signUp, loading } = useAuthCtx();
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const initialMode = search?.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState<"login" | "register">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [resending, setResending] = useState(false);

  const resendConfirmation = async (target: string) => {
    if (!target) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: target,
        options: { emailRedirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) {
        toast.error("Não foi possível reenviar agora. Tente novamente em alguns minutos.");
      } else {
        toast.success("Email de confirmação reenviado. Verifique sua caixa de entrada e o spam.");
      }
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (user) navigate({ to: "/dashboard" });
  }, [user, navigate]);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/dashboard",
      });
      if (result.error) {
        toast.error("Não foi possível entrar com o Google. Tente novamente.");
        setGoogleLoading(false);
        return;
      }
      // result.redirected -> browser will redirect; otherwise tokens already set.
    } catch {
      toast.error("Não foi possível entrar com o Google. Tente novamente.");
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    if (mode === "register" && password.length < 8) {
      toast.error("Use pelo menos 8 caracteres na senha.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "register") {
        const { data, error } = await signUp(email, password, name || undefined);
        if (error) {
          if (import.meta.env.DEV) {
            console.error("Sign-up error:", error);
          }
          const msg = error.message?.toLowerCase() ?? "";
          if (msg.includes("already registered") || msg.includes("already been registered") || msg.includes("user already")) {
            toast.error("Este email já está cadastrado. Tente fazer login.");
          } else if (msg.includes("pwned") || msg.includes("compromised") || msg.includes("breach")) {
            toast.error("Essa senha apareceu em vazamentos públicos. Escolha outra mais segura.");
          } else if (msg.includes("password")) {
            toast.error("Senha inválida. Use pelo menos 8 caracteres com letra e número.");
          } else if (msg.includes("email")) {
            toast.error("Email inválido. Verifique e tente novamente.");
          } else {
            toast.error("Não foi possível criar a conta. Tente novamente.");
          }
        } else if (data?.session) {
          // Confirmação de email desativada — entra direto.
          toast.success("Conta criada com sucesso!");
        } else {
          setConfirmEmail(email);
          setConfirmOpen(true);
          setMode("login");
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          if (import.meta.env.DEV) {
            console.error("Sign-in error:", error);
          }
          const msg = error.message?.toLowerCase() ?? "";
          if (msg.includes("not confirmed") || msg.includes("email_not_confirmed") || msg.includes("confirm your email")) {
            setConfirmEmail(email);
            setConfirmOpen(true);
            toast.error("Seu email ainda não foi confirmado. Verifique sua caixa de entrada.");
          } else if (msg.includes("invalid login credentials") || msg.includes("invalid_credentials")) {
            toast.error("Email ou senha inválidos.");
          } else if (msg.includes("rate limit") || msg.includes("too many")) {
            toast.error("Muitas tentativas. Aguarde alguns minutos e tente de novo.");
          } else {
            toast.error("Não foi possível entrar. Tente novamente.");
          }
        } else {
          // Fire-and-forget welcome email — sent from an authenticated context so
          // the server derives the recipient from the session, not client input.
          sendWelcomeEmail({ data: { name: name || undefined } }).catch(() => {});
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="size-12 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-2">
              <MailCheck className="size-6 text-primary-glow" />
            </div>
            <DialogTitle className="font-display text-xl">Confirme seu email</DialogTitle>
            <DialogDescription className="leading-relaxed">
              Enviamos um link de confirmação para{" "}
              <span className="text-foreground font-medium">{confirmEmail}</span>. Abra o email e
              clique no link para ativar sua conta. Se não encontrar, verifique a caixa de spam ou
              promoções.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => resendConfirmation(confirmEmail)}
              disabled={resending}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-border hover:border-primary/40 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {resending ? <Loader2 className="size-4 animate-spin" /> : <Mail className="size-4" />}
              Reenviar email
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-gradient-primary text-primary-foreground text-sm font-semibold"
            >
              Entendi
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="lg:flex-1 relative overflow-hidden p-8 lg:p-16 flex flex-col justify-between min-h-[30vh] lg:min-h-screen bg-gradient-card border-b lg:border-b-0 lg:border-r border-border/40">
        <div className="absolute inset-0 bg-gradient-glow opacity-60 pointer-events-none" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft className="size-4" /> Voltar ao início
          </Link>
          <div className="flex items-center gap-3">
            <BrandLogo className="size-12" priority />
            <div>
              <p className="font-display font-bold text-xl leading-tight">Seu Bolso</p>
              <p className="text-sm text-muted-foreground leading-tight">Tranquilo</p>
            </div>
          </div>
        </div>

        <div className="relative max-w-lg hidden lg:block">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/15 border border-primary/30 text-xs font-medium text-primary-glow mb-6">
            <Sparkles className="size-3" /> Comece hoje, grátis
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight mb-4">
            Sua vida financeira <span className="text-gradient">organizada</span> em minutos.
          </h2>
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            Insights automáticos, gráficos claros e zero complicação.
          </p>
        </div>

        <div className="relative hidden lg:flex gap-4 text-xs text-muted-foreground">
          <span>✨ Insights automáticos</span>
          <span>📊 Gráficos claros</span>
          <span>🎯 Metas pessoais</span>
        </div>
      </div>

      <div className="lg:w-[440px] p-8 lg:p-12 flex items-center">
        <form onSubmit={handleSubmit} className="w-full space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold">
              {mode === "login" ? "Bem-vindo de volta" : "Crie sua conta"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === "login" ? "Entre para acessar seu painel" : "Leva menos de um minuto"}
            </p>
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading || submitting}
            className="w-full py-3 rounded-2xl bg-background border border-border hover:border-primary/40 hover:bg-surface-elevated transition-all inline-flex items-center justify-center gap-2.5 text-sm font-medium disabled:opacity-60"
          >
            {googleLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" fill="#34A853"/>
                <path d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" fill="#EA4335"/>
              </svg>
            )}
            Continuar com Google
          </button>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border/60" />
            ou com email
            <div className="flex-1 h-px bg-border/60" />
          </div>

          {mode === "register" && (
            <div className="space-y-1.5">
              <label htmlFor="auth-name" className="text-xs font-medium text-muted-foreground">Nome</label>
              <input
                id="auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Como você se chama?"
                className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="auth-email" className="text-xs font-medium text-muted-foreground">Email</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@email.com"
              required
              className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="auth-password" className="text-xs font-medium text-muted-foreground">Senha</label>
            <div className="relative">
              <input
                id="auth-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={mode === "register" ? 8 : 6}
                className="w-full px-4 py-3 pr-12 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            {mode === "register" && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Mínimo 8 caracteres, com letra e número.
              </p>
            )}
          </div>

          {mode === "login" && (
            <div className="text-right -mt-2">
              <Link
                to="/esqueci-senha"
                className="text-xs text-muted-foreground hover:text-primary-glow transition-colors"
              >
                Esqueci minha senha
              </Link>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || loading}
            className="w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {mode === "login" ? "Entrar" : "Criar conta"}
          </button>

          {mode === "register" && (
            <p className="text-center text-xs text-muted-foreground leading-relaxed">
              Ao criar uma conta, você concorda com nossos{" "}
              <Link to="/termos" className="text-primary-glow hover:underline">
                Termos de uso
              </Link>{" "}
              e{" "}
              <Link to="/privacidade" className="text-primary-glow hover:underline">
                Política de Privacidade
              </Link>
              .
            </p>
          )}

          <p className="text-center text-sm text-muted-foreground">
            {mode === "login" ? "Não tem conta?" : "Já tem conta?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary-glow hover:underline font-medium"
            >
              {mode === "login" ? "Criar conta" : "Fazer login"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}